import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const HumanBody: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/HumanBody.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/HumanBody.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "The Human Body – Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn about the Human Body for Class 3 Science with simple explanations, diagrams, and interactive content. Discover body parts and their functions at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1 className="mb-5">🧍‍♂️Chapter 2: Human Body</h1>

                            <section>
                                <h2><span className="emoji">👀</span>Internal and External Organs</h2>

                                <h3>External Organs:</h3>
                                <ul>
                                    <li><strong>Eyes</strong> – Help us see</li>
                                    <li><strong>Ears</strong> – Help us hear</li>
                                    <li><strong>Nose</strong> – Helps us smell and breathe</li>
                                    <li><strong>Hands and Legs</strong> – Help us move and do work</li>
                                    <li><strong>Skin</strong> – Covers our body and helps us feel</li>
                                </ul>

                                <h3>Internal Organs:</h3>
                                <ul>
                                    <li><strong>Brain</strong> – Controls all actions</li>
                                    <li><strong>Heart</strong> – Pumps blood</li>
                                    <li><strong>Lungs</strong> – Help us breathe</li>
                                    <li><strong>Stomach</strong> – Digests food</li>
                                    <li><strong>Liver and Kidneys</strong> – Filter and clean the blood</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">⚙️</span>Functions of Organ Systems</h2>
                                <ul>
                                    <li><strong>Digestive System</strong> 🍽️ – Breaks down food for energy</li>
                                    <li><strong>Respiratory System</strong> 🌬️ – Helps us breathe in oxygen and breathe out carbon dioxide</li>
                                    <li><strong>Circulatory System</strong> ❤️ – Moves blood and oxygen around the body</li>
                                    <li><strong>Nervous System</strong> 🧠 – Controls our body through the brain and nerves</li>
                                    <li><strong>Excretory System</strong> 🚽 – Removes waste from the body</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🌬️</span>Why Do We Need to Breathe?</h2>
                                <p>Breathing gives our body the oxygen it needs to make energy. We take in <strong>oxygen</strong> and remove <strong>carbon dioxide</strong> through our lungs. It’s a process that happens all the time — even when we sleep!</p>
                            </section>

                            <section>
                                <h2><span className="emoji">🌫️</span>Causes and Problems of Air Pollution</h2>

                                <h3>Causes:</h3>
                                <ul>
                                    <li>Smoke from cars and buses 🚗</li>
                                    <li>Gases from factories 🏭</li>
                                    <li>Burning garbage 🔥</li>
                                    <li>Sprays and chemicals 🧪</li>
                                </ul>

                                <h3>Problems:</h3>
                                <ul>
                                    <li>Breathing trouble and asthma</li>
                                    <li>Cough, eye irritation, skin allergies</li>
                                    <li>Harm to birds, animals, and plants</li>
                                    <li>Unhealthy and dirty environment</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🌱</span>How Can We Reduce Air Pollution?</h2>
                                <ul>
                                    <li>Ride a bicycle or walk when possible 🚲</li>
                                    <li>Plant more trees 🌳</li>
                                    <li>Use public transport 🚌</li>
                                    <li>Don’t burn plastic or trash ♻️</li>
                                    <li>Save electricity and energy 🔌</li>
                                </ul>
                            </section>

                            <section className="summary">
                                <h2><span className="emoji">🧠</span>Summary</h2>
                                <ul>
                                    <li>Our body has both internal and external organs.</li>
                                    <li>Organ systems like the brain, heart, and lungs help us stay alive and active.</li>
                                    <li>Breathing is important to give energy to the body.</li>
                                    <li>Air pollution is harmful and caused by smoke and waste.</li>
                                    <li>We should all take steps to reduce air pollution and protect our health.</li>
                                </ul>
                            </section>

                            <section className="activity">
                                <h2><span className="emoji">📚</span>Fun Activities</h2>
                                <ul>
                                    <li>🖍️ Draw and label 5 external and 5 internal organs of the body.</li>
                                    <li>📊 Make a chart of different organ systems and their functions.</li>
                                    <li>🎨 Create a poster showing “Ways to Reduce Air Pollution.”</li>
                                </ul>
                            </section>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🧍‍♂️ Class 3 Science – Chapter 2: Human Body</h1>

                        <section>
                            <h2><span className="emoji">👀</span>Internal and External Organs</h2>

                            <h3>External Organs:</h3>
                            <ul>
                                <li><strong>Eyes</strong> – Help us see</li>
                                <li><strong>Ears</strong> – Help us hear</li>
                                <li><strong>Nose</strong> – Helps us smell and breathe</li>
                                <li><strong>Hands and Legs</strong> – Help us move and do work</li>
                                <li><strong>Skin</strong> – Covers our body and helps us feel</li>
                            </ul>

                            <h3>Internal Organs:</h3>
                            <ul>
                                <li><strong>Brain</strong> – Controls all actions</li>
                                <li><strong>Heart</strong> – Pumps blood</li>
                                <li><strong>Lungs</strong> – Help us breathe</li>
                                <li><strong>Stomach</strong> – Digests food</li>
                                <li><strong>Liver and Kidneys</strong> – Filter and clean the blood</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">⚙️</span>Functions of Organ Systems</h2>
                            <ul>
                                <li><strong>Digestive System</strong> 🍽️ – Breaks down food for energy</li>
                                <li><strong>Respiratory System</strong> 🌬️ – Helps us breathe in oxygen and breathe out carbon dioxide</li>
                                <li><strong>Circulatory System</strong> ❤️ – Moves blood and oxygen around the body</li>
                                <li><strong>Nervous System</strong> 🧠 – Controls our body through the brain and nerves</li>
                                <li><strong>Excretory System</strong> 🚽 – Removes waste from the body</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🌬️</span>Why Do We Need to Breathe?</h2>
                            <p>Breathing gives our body the oxygen it needs to make energy. We take in <strong>oxygen</strong> and remove <strong>carbon dioxide</strong> through our lungs. It’s a process that happens all the time — even when we sleep!</p>
                        </section>

                        <section>
                            <h2><span className="emoji">🌫️</span>Causes and Problems of Air Pollution</h2>

                            <h3>Causes:</h3>
                            <ul>
                                <li>Smoke from cars and buses 🚗</li>
                                <li>Gases from factories 🏭</li>
                                <li>Burning garbage 🔥</li>
                                <li>Sprays and chemicals 🧪</li>
                            </ul>

                            <h3>Problems:</h3>
                            <ul>
                                <li>Breathing trouble and asthma</li>
                                <li>Cough, eye irritation, skin allergies</li>
                                <li>Harm to birds, animals, and plants</li>
                                <li>Unhealthy and dirty environment</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🌱</span>How Can We Reduce Air Pollution?</h2>
                            <ul>
                                <li>Ride a bicycle or walk when possible 🚲</li>
                                <li>Plant more trees 🌳</li>
                                <li>Use public transport 🚌</li>
                                <li>Don’t burn plastic or trash ♻️</li>
                                <li>Save electricity and energy 🔌</li>
                            </ul>
                        </section>

                        <section className="summary">
                            <h2><span className="emoji">🧠</span>Summary</h2>
                            <ul>
                                <li>Our body has both internal and external organs.</li>
                                <li>Organ systems like the brain, heart, and lungs help us stay alive and active.</li>
                                <li>Breathing is important to give energy to the body.</li>
                                <li>Air pollution is harmful and caused by smoke and waste.</li>
                                <li>We should all take steps to reduce air pollution and protect our health.</li>
                            </ul>
                        </section>

                        <section className="activity">
                            <h2><span className="emoji">📚</span>Fun Activities</h2>
                            <ul>
                                <li>🖍️ Draw and label 5 external and 5 internal organs of the body.</li>
                                <li>📊 Make a chart of different organ systems and their functions.</li>
                                <li>🎨 Create a poster showing “Ways to Reduce Air Pollution.”</li>
                            </ul>
                        </section>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default HumanBody;
